import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { API_URL } from '../../utils/apiUrl';
import { useTheme } from '../../contexts/ThemeContext';

const PrivacyPage = () => {
  const { t, i18n } = useTranslation('auth');
  const { theme, getGradient } = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPrivacy = async () => {
      try {
        setLoading(true);
        const lang = i18n.language?.startsWith('hr') ? 'hr' : 'en';
        const response = await fetch(`${API_URL}/public/privacy?lang=${lang}`);
        const data = await response.json();
        setContent(data.content || getDefaultPrivacy(lang));
      } catch (error) {
        console.error('Failed to fetch privacy policy:', error);
        setContent(getDefaultPrivacy(i18n.language?.startsWith('hr') ? 'hr' : 'en'));
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacy();
  }, [i18n.language]);

  const getDefaultPrivacy = (lang) => {
    if (lang === 'hr') {
      return `POLITIKA PRIVATNOSTI

Posljednje ažurirano: ${new Date().toLocaleDateString('hr-HR')}

1. UVOD
Ova Politika privatnosti objašnjava kako prikupljamo, koristimo i štitimo vaše osobne podatke kada koristite našu fitness aplikaciju.

2. PODACI KOJE PRIKUPLJAMO
Možemo prikupljati sljedeće informacije:
- Ime i adresa e-pošte
- Podaci o treninzima i napretku
- Fotografije napretka (ako ih podijelite)
- Poruke s trenerom

3. KAKO KORISTIMO VAŠE PODATKE
Vaši podaci koriste se za:
- Pružanje personaliziranih planova treninga
- Praćenje vašeg fitness napretka
- Komunikaciju između vas i vašeg trenera
- Poboljšanje naših usluga

4. DIJELJENJE PODATAKA
Vaše osobne podatke ne prodajemo niti iznajmljujemo trećim stranama. Vaši podaci mogu se dijeliti samo s:
- Vašim dodijeljenim trenerom
- Administratorima sustava (za tehničku podršku)

5. SIGURNOST PODATAKA
Implementiramo odgovarajuće sigurnosne mjere za zaštitu vaših osobnih podataka od neovlaštenog pristupa ili otkrivanja.

6. VAŠA PRAVA
Imate pravo:
- Pristupiti svojim osobnim podacima
- Ispraviti netočne podatke
- Zatražiti brisanje svojih podataka
- Povući privolu u bilo kojem trenutku

7. KONTAKT
Za pitanja o ovoj Politici privatnosti kontaktirajte svog trenera ili administratora sustava.`;
    }
    
    return `PRIVACY POLICY

Last updated: ${new Date().toLocaleDateString('en-US')}

1. INTRODUCTION
This Privacy Policy explains how we collect, use, and protect your personal information when you use our fitness application.

2. INFORMATION WE COLLECT
We may collect the following information:
- Name and email address
- Workout and progress data
- Progress photos (if you choose to share)
- Messages with your trainer

3. HOW WE USE YOUR DATA
Your data is used to:
- Provide personalized training plans
- Track your fitness progress
- Enable communication between you and your trainer
- Improve our services

4. DATA SHARING
We do not sell or rent your personal information to third parties. Your data may only be shared with:
- Your assigned trainer
- System administrators (for technical support)

5. DATA SECURITY
We implement appropriate security measures to protect your personal information from unauthorized access or disclosure.

6. YOUR RIGHTS
You have the right to:
- Access your personal data
- Correct inaccurate data
- Request deletion of your data
- Withdraw consent at any time

7. CONTACT
For questions about this Privacy Policy, please contact your trainer or system administrator.`;
  };

  const formatContent = (text) => {
    if (!text) return null;
    
    return text.split('\n').map((line, index) => {
      // Headers (all caps lines)
      if (line === line.toUpperCase() && line.trim().length > 0 && !line.match(/^\d+\./)) {
        return (
          <h2 key={index} className="text-xl font-bold text-gray-900 mt-6 mb-3">
            {line}
          </h2>
        );
      }
      // Numbered sections
      if (line.match(/^\d+\.\s+[A-Z]/)) {
        return (
          <h3 key={index} className="text-lg font-semibold text-gray-800 mt-4 mb-2">
            {line}
          </h3>
        );
      }
      // Bullet points
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        return (
          <li key={index} className="ml-6 text-gray-700 mb-1">
            {line.replace(/^[-•]\s*/, '')}
          </li>
        );
      }
      // Empty lines
      if (line.trim() === '') {
        return <div key={index} className="h-2" />;
      }
      // Regular paragraphs
      return (
        <p key={index} className="text-gray-700 mb-2">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Helmet>
        <title>Pravila Privatnosti | ProTrener</title>
        <meta name="description" content="Pravila privatnosti ProTrener aplikacije. Saznajte kako prikupljamo i štitimo vaše osobne podatke." />
        <link rel="canonical" href="https://protrener.app/privacy" />
        <meta property="og:title" content="Pravila Privatnosti | ProTrener" />
        <meta property="og:description" content="Pravila privatnosti ProTrener aplikacije." />
        <meta property="og:image" content="https://protrener.app/favicon.ico" />
        <meta property="og:url" content="https://protrener.app/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="hr_HR" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Pravila Privatnosti | ProTrener" />
        <meta name="twitter:description" content="Pravila privatnosti ProTrener aplikacije." />
      </Helmet>
      {/* Header */}
      <div className={`bg-gradient-to-r ${getGradient(0)} py-6 px-4 native-safe-top`}>
        <div className="max-w-3xl mx-auto">
          <Link
            to="/login"
            className="inline-flex items-center text-white/80 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            {t('legal.backToLogin', 'Back to Login')}
          </Link>
          <div className="flex items-center">
            <ShieldCheckIcon className="h-8 w-8 text-white mr-3" />
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              {t('legal.privacyPolicy', 'Privacy Policy')}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="prose prose-gray max-w-none">
              {formatContent(content)}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <Link
            to="/terms"
            className={`${theme.sectionLink} font-semibold`}
          >
            {t('legal.viewTermsOfService', 'View Terms of Service')} →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
